/**
 * @ Author: Housefolios
 * @ Create Time: 2021-10-05 11:07:32
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-08 12:28:48
 * @ Description: Dialog to cancel subscription and become a free account
 */

import React, { useState } from 'react'
// Centralized lodash imports - much cleaner!
import {
  capitalize,
  split,
  replace,
  round,
} from '@/utils/lodash'

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import withSession from '@/housefolios-components/Session/withSession'
import { useMutation } from '@apollo/client/react'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import {
  CANCEL_SUBSCRIPTION,
  DELETE_SUBSCRIPTION,
  STOPCANCEL_SUBSCRIPTION,
} from '@/housefolios-components/zComponents/mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { enqueueSnackbar } from 'notistack'
import { useNavigate } from 'react-router'

import {
  isStripeSubscriptionId,
  getNextBillingDateLabel,
} from '@/utils/subscriptionBilling'

function CancelButton(props) {
  const { title, session } = props
  const { activeOrg } = session.me
  const navigate = useNavigate()

  const [showCancel, setShowCancel] = useState(false)

  const toggleCancel = () => {
    setShowCancel(!showCancel)
  }

  const [loader, setLoader] = useState(false)

  const [showVerifyCancel, setShowVerifyCancel] = useState(false)

  const toggleVerifyCancel = () => {
    setLoader(false)
    setShowVerifyCancel(!showVerifyCancel)
  }

  const [showCoachingCancel, setShowCoachingCancel] = useState(false)

  const toggleCoachingCancel = () => {
    setLoader(false)
    setShowCoachingCancel(!showCoachingCancel)
  }

  const subscriptions = Array.isArray(activeOrg.subscriptions)
    ? activeOrg.subscriptions
    : []
  const activeStripeSub =
    subscriptions.find(
      (sub) => !sub?.disabled && isStripeSubscriptionId(sub?.id),
    ) ||
    subscriptions.find((sub) => isStripeSubscriptionId(sub?.id)) ||
    null
  const stripeSubId = activeStripeSub?.id || null

  const [plan, billing] = props.session.me.subscriptionPlan
    ? split(replace(props.session.me.subscriptionPlan, '_', '-'), '-')
    : ''

  const dateString =
    getNextBillingDateLabel(activeStripeSub) || 'your next billing date'

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const toBeCanceled = Boolean(activeStripeSub?.cancel_at_period_end)

  const [cancelBeforeCharge, setCancelBeforeCharge] = useState(true)

  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION, {
    variables: {
      id: activeOrg._id,
      organizationInput: {
        disabled: false,
        delinquent: '',
      },
    },
    refetchQueries: [
      {
        query: GET_ME,
      },
    ],
  })

  const [deleteSubscription] = useMutation(DELETE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_ME }],
  })
  const [cancelSubscription] = useMutation(CANCEL_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_ME }],
  })
  const [stopCancelSubscription] = useMutation(
    STOPCANCEL_SUBSCRIPTION,
    {
      refetchQueries: [{ query: GET_ME }],
    },
  )

  return (
    <>
      {title && stripeSubId && (
        <Button
          variant="contained"
          size="small"
         
          style={{ fontWeight: 600 }}
          onClick={
            billing == 'coaching'
              ? toggleCoachingCancel
              : toggleVerifyCancel
          }
         sx={{
           boxShadow: "0 0.25rem 0.55rem rgba(248, 50, 69, 0.35)",
           color: "#fff",
           backgroundColor: "#f83245",
           backgroundImage: "none",
           borderColor: "#f83245",
           "&:not(:disabled):hover": { backgroundColor: "#f83245", color: "#fff" },
           "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#ef081e" },
           "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(248, 50, 69, 0.4), 0 0.0625rem 0.385rem rgba(248, 50, 69, 0.54)" },
           "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
         }}>
          {/* <Close style={{ color: 'white', marginRight: '5px' }} /> */}

          <FontAwesomeIcon icon={['fas', 'xmark']} style={{ marginRight: '10px' }} sx={{ fontSize: "1rem !important" }} />
          <Box component="span" style={{ fontWeight: 600 }} sx={{
            display: "inline-block",
            "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
            "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
          }}>
            {toBeCanceled
              ? `Plan cancels on ${dateString}`
              : 'Cancel Account'}
          </Box>
        </Button>
      )}

      <Dialog
        open={showCoachingCancel}
        onClose={toggleCoachingCancel}
      >
        <DialogTitle>Verify Account</DialogTitle>
        <DialogContent>
          <Grid
            container
            direction="column"
            spacing={2}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography>
                As a coaching plan we cannot automatically cancel your
                subscription. If you wish to cancel contact us through
                support
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={toggleCoachingCancel}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showVerifyCancel} onClose={toggleVerifyCancel}>
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={<Box sx={{ color: '#368cff' }}>Please Wait</Box>}
        >
          <DialogTitle>Verify Account</DialogTitle>
          <DialogContent>
            <Grid
              container
              direction="column"
              spacing={2}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Grid>
                <Typography>
                  Are you sure you want to cancel? We would love you
                  to stay!
                </Typography>
              </Grid>
              <Grid>
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  size="small"
                  value={cancelBeforeCharge ? 'period' : 'now'}
                  onChange={(_event, value) => {
                    if (value == null) return
                    setCancelBeforeCharge(value === 'period')
                  }}
                >
                  <ToggleButton value="now">Cancel Now</ToggleButton>
                  <ToggleButton value="period">
                    {toBeCanceled ? 'Stop Cancel' : 'Before Next Charge'}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {!cancelBeforeCharge ? (
                <Grid>
                  <Typography>
                    Cancelation will occur today. Your account will
                    become a free account and you will lose all
                    features of a paid plan.
                  </Typography>
                </Grid>
              ) : !toBeCanceled ? (
                <Grid>
                  <Typography>
                    Cancelation will occur on {dateString}. Your
                    account will become a free account and you will
                    lose all features of a paid plan.
                  </Typography>
                </Grid>
              ) : (
                <Grid>
                  <Typography>
                    Your {capitalize(plan)} plan and will not be
                    canceled and your credit card will be charged{' '}
                    {currencyFormat.format(
                      round(props.session.me.subscriptionPrice / 100),
                    )}{' '}
                    on the next billing period: {dateString}.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          {!cancelBeforeCharge ? (
            <DialogActions>
              <Button
                variant="contained"
                sx={{
                  boxShadow: "0 0.25rem 0.55rem rgba(248, 50, 69, 0.35)",
                  color: "#fff",
                  backgroundColor: "#f83245",
                  backgroundImage: "none",
                  borderColor: "#f83245",
                  "&:not(:disabled):hover": { backgroundColor: "#f83245", color: "#fff" },
                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#ef081e" },
                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(248, 50, 69, 0.4), 0 0.0625rem 0.385rem rgba(248, 50, 69, 0.54)" },
                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                }}
                onClick={() => {
                  if (!stripeSubId) {
                    enqueueSnackbar(
                      'No active Stripe subscription found to cancel.',
                      { variant: 'error' },
                    )
                    return
                  }
                  setLoader(true)
                  deleteSubscription({
                    variables: { id: stripeSubId },
                  })
                    .then(() => updateOrganization())
                    .then(() => {
                      window.open(
                        'https://docs.google.com/forms/d/e/1FAIpQLSeY8GKTrOwKREf3t41W0N49whxMPylFUsQG4mrQqMQQDJH99A/viewform?usp=sf_link',
                        '_blank',
                      )
                      toggleVerifyCancel()
                      try {
                        navigate('/deactivated', { replace: true  })
                      } catch (e) {}
                      window.location.assign('/deactivated')
                    })
                    .catch((error) => {
                      setLoader(false)
                      enqueueSnackbar(
                        error?.message ||
                          'Could not cancel subscription.',
                        { variant: 'error' },
                      )
                    })
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={toggleVerifyCancel}
              >
                {toBeCanceled ? 'Close' : 'Keep Plan'}
              </Button>
            </DialogActions>
          ) : !toBeCanceled ? (
            <DialogActions>
              <Button
                variant="contained"
                sx={{
                  boxShadow: "0 0.25rem 0.55rem rgba(248, 50, 69, 0.35)",
                  color: "#fff",
                  backgroundColor: "#f83245",
                  backgroundImage: "none",
                  borderColor: "#f83245",
                  "&:not(:disabled):hover": { backgroundColor: "#f83245", color: "#fff" },
                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#ef081e" },
                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(248, 50, 69, 0.4), 0 0.0625rem 0.385rem rgba(248, 50, 69, 0.54)" },
                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                }}
                onClick={() => {
                  if (!stripeSubId) {
                    enqueueSnackbar(
                      'No active Stripe subscription found to cancel.',
                      { variant: 'error' },
                    )
                    return
                  }
                  setLoader(true)
                  cancelSubscription({
                    variables: { id: stripeSubId },
                  })
                    .then(() => updateOrganization())
                    .then(() => {
                      window.open(
                        'https://forms.gle/Cbr73Eac1gvDZePd8',
                        '_blank',
                      )
                      enqueueSnackbar(
                        `Your plan will remain active until ${dateString}.`,
                        { variant: 'success' },
                      )
                      toggleVerifyCancel()
                    })
                    .catch((error) => {
                      setLoader(false)
                      enqueueSnackbar(
                        error?.message ||
                          'Could not cancel subscription.',
                        { variant: 'error' },
                      )
                    })
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={toggleVerifyCancel}
              >
                Keep Plan
              </Button>
            </DialogActions>
          ) : (
            <DialogActions>
              <Button color="primary" onClick={toggleVerifyCancel}>
                Close
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  if (!stripeSubId) {
                    enqueueSnackbar(
                      'No active Stripe subscription found.',
                      { variant: 'error' },
                    )
                    return
                  }
                  setLoader(true)
                  stopCancelSubscription({
                    variables: { id: stripeSubId },
                  })
                    .then(() => updateOrganization())
                    .then(() => {
                      enqueueSnackbar('Cancellation was stopped.', {
                        variant: 'success',
                      })
                      toggleVerifyCancel()
                    })
                    .catch((error) => {
                      setLoader(false)
                      enqueueSnackbar(
                        error?.message ||
                          'Could not stop cancellation.',
                        { variant: 'error' },
                      )
                    })
                }}
              >
                Stop Cancel
              </Button>
            </DialogActions>
          )}
        </BlockUi>
      </Dialog>
    </>
  )
}

export default withSession(CancelButton)
